import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, FlaskConical, Clock, Sparkles, Camera } from "lucide-react";
import LitLabLayout from "../components/LitLabLayout";
import LitLabQrCard from "../components/LitLabQrCard";
import { litlabCaseStudies, litlabCaseStudy, litlabCaseStudyGenerate, litlabCaseStudyImageUrl } from "../api";

// Fallback micro imagery when AI generation hasn't run yet — sourced ONLY
// from litlab.com's own asset library (per customer requirement: no stock).
const FALLBACK_IMAGES = {
  "turbine-blade-hcf-failure":                  "https://www.litlab.com/files/slides/4053127774.JPG",
  "automotive-fastener-hydrogen-embrittlement": "https://www.litlab.com/userfiles/1554490124_Automotive%20(2).jpg",
  "welded-frame-litigation-support":            "https://www.litlab.com/files/slides/7482738499.JPG",
  "titanium-alpha-case-rejection":              "https://www.litlab.com/userfiles/1554489831_Nadcap.JPG",
  "electronics-solder-void-sem-eds":            "https://www.litlab.com/userfiles/1555438971_Electronic%20Part_266X_2.jpg",
};

// Universal generic fallback if every source fails — a lab-floor photo from
// litlab.com's own gallery.
const GENERIC_FALLBACK = "https://www.litlab.com/files/slides/1177685004.JPG";

export function LitLabCaseStudies() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    document.title = "Case Studies | L.I.T. Labs";
    litlabCaseStudies().then((d) => setItems(d.items || []));
  }, []);
  return (
    <LitLabLayout>
      <section className="pt-16 pb-12 bg-white border-b border-sky-200" data-testid="litlab-cases">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-700 font-bold">Case studies</div>
          <h1 className="mt-3 litlab-serif text-4xl md:text-6xl font-bold text-slate-900 max-w-3xl">
            Real problems. Metallurgical answers.
          </h1>
          <p className="mt-5 text-slate-600 max-w-2xl">
            Anonymized case studies from our aerospace, automotive, litigation and electronics customers — the kinds of failure analyses that pay for themselves in a single decision.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-700 font-bold bg-amber-50 border border-amber-300 rounded px-2 py-1">
            <Sparkles className="w-3 h-3" /> AI-illustrated micro imagery
          </div>
        </div>
      </section>

      <section className="py-16 bg-sky-50">
        <div className="max-w-6xl mx-auto px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((c) => (
            <Link key={c.slug} to={`/litlab/case-studies/${c.slug}`}
              data-testid={`litlab-case-${c.slug}`}
              className="group rounded-2xl border border-sky-200 bg-white overflow-hidden hover:border-sky-500 hover:shadow-lg transition">
              <div className="relative aspect-video overflow-hidden bg-slate-100">
                <img src={c.image_status === "ready" ? litlabCaseStudyImageUrl(c.slug) : FALLBACK_IMAGES[c.slug]}
                      alt={c.title}
                      className="w-full h-full object-cover transition duration-500 group-hover:scale-105"
                      onError={(e) => {
                        // Chain: AI image → per-slug stock → generic litlab lab shot
                        if (e.currentTarget.src !== FALLBACK_IMAGES[c.slug]) {
                          e.currentTarget.src = FALLBACK_IMAGES[c.slug];
                        } else {
                          e.currentTarget.src = GENERIC_FALLBACK;
                        }
                      }} />
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className="text-[10px] uppercase tracking-widest font-bold bg-white/90 text-sky-700 rounded px-2 py-0.5">{c.industry}</span>
                  {c.image_status === "ready" && (
                    <span className="text-[10px] uppercase tracking-widest font-bold bg-amber-500 text-white rounded px-2 py-0.5 inline-flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5" /> AI
                    </span>
                  )}
                </div>
              </div>
              <div className="p-5">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {c.duration_weeks} weeks
                  <span>·</span>
                  <FlaskConical className="w-3 h-3" /> {c.service}
                </div>
                <div className="mt-2 litlab-serif text-xl font-bold text-slate-900 group-hover:text-sky-700 transition leading-snug">{c.title}</div>
                <div className="mt-2 text-sm text-slate-600 line-clamp-2">{c.problem}</div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </LitLabLayout>
  );
}

export function LitLabCaseStudyDetail() {
  const { slug } = useParams();
  const [cs, setCs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [imgKey, setImgKey] = useState(0);
  const load = () => litlabCaseStudy(slug).then(setCs).catch(() => setCs(false));
  useEffect(() => { load(); }, [slug]);

  const generate = async () => {
    setBusy(true); setNotice("Generating AI micro imagery (Gemini Nano Banana) — takes ~15s…");
    try {
      await litlabCaseStudyGenerate(slug);
      setNotice("");
      setImgKey((k) => k + 1);
      load();
    } catch (e) {
      setNotice(e?.response?.data?.detail || "Generation requires admin sign-in.");
    } finally { setBusy(false); }
  };

  if (cs === false) return <LitLabLayout><div className="pt-32 text-center text-slate-500">Case study not found.</div></LitLabLayout>;
  if (!cs) return <LitLabLayout><div className="pt-20 text-center text-slate-500">Loading…</div></LitLabLayout>;

  const heroSrc = cs.image_status === "ready"
    ? `${litlabCaseStudyImageUrl(slug)}?v=${imgKey}`
    : FALLBACK_IMAGES[slug];

  return (
    <LitLabLayout>
      <section className="pt-16 pb-8 bg-white border-b border-sky-200">
        <div className="max-w-4xl mx-auto px-6">
          <Link to="/litlab/case-studies"
            className="text-xs uppercase tracking-[0.3em] font-bold text-sky-700 inline-flex items-center gap-1 hover:text-sky-800">
            <ArrowLeft className="w-3 h-3" /> Case studies
          </Link>
          <div className="mt-4 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-widest font-bold">
            <span className="text-slate-500">{cs.industry}</span><span className="text-slate-300">/</span>
            <span className="text-slate-500">{cs.service}</span><span className="text-slate-300">/</span>
            <span className="text-slate-500 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {cs.duration_weeks} weeks</span>
          </div>
          <h1 className="mt-2 litlab-serif text-3xl md:text-5xl font-bold text-slate-900">{cs.title}</h1>
        </div>
      </section>

      <section className="py-8 bg-sky-50" data-testid={`litlab-case-detail-${slug}`}>
        <div className="max-w-4xl mx-auto px-6">
          <div className="relative rounded-2xl overflow-hidden border border-sky-200 shadow-lg bg-white">
            <img src={heroSrc} alt={cs.title} className="w-full aspect-[16/9] object-cover" data-testid="litlab-case-image"
              onError={(e) => {
                if (e.currentTarget.src !== FALLBACK_IMAGES[slug]) {
                  e.currentTarget.src = FALLBACK_IMAGES[slug];
                } else {
                  e.currentTarget.src = GENERIC_FALLBACK;
                }
              }} />
            <div className="absolute top-3 right-3 flex gap-2 flex-wrap justify-end">
              {cs.image_status === "ready" ? (
                <span className="text-[10px] uppercase tracking-widest font-bold bg-amber-500 text-white rounded px-2 py-1 inline-flex items-center gap-1">
                  <Sparkles className="w-3 h-3" /> AI micrograph
                </span>
              ) : (
                <button onClick={generate} disabled={busy}
                  data-testid="litlab-case-generate"
                  className="text-[10px] uppercase tracking-widest font-bold bg-sky-700 hover:bg-sky-600 disabled:opacity-40 text-white rounded px-3 py-1.5 inline-flex items-center gap-1 shadow-sm">
                  <Camera className="w-3 h-3" /> {busy ? "Generating…" : "Generate AI micrograph"}
                </button>
              )}
            </div>
          </div>
          {notice && <div className="mt-2 text-xs text-slate-600">{notice}</div>}

          <div className="mt-6">
            <LitLabQrCard
              productionUrl={`https://websitetech.pro/litlab/case-studies/${slug}`}
              subtitle="Scan to open this case study on any phone"
              testId={`case-${slug}`}
            />
          </div>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-6 space-y-8">
          <Block label="Problem"  body={cs.problem} tone="rose" />
          <Block label="Approach" body={cs.approach} tone="sky" />
          <Block label="Finding"  body={cs.finding} tone="amber" />
          <Block label="Outcome"  body={cs.outcome} tone="emerald" checkmark />
        </div>
      </section>

      <section className="py-16 bg-sky-50 border-t border-sky-200">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="litlab-serif text-3xl md:text-4xl font-bold text-slate-900">Have a similar problem?</h2>
          <p className="mt-3 text-slate-600">Every case starts with a phone call and a sample. Most quotes go out same-day.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link to={`/litlab/quote?service=${cs.service}&industry=${cs.industry}`}
              data-testid="litlab-case-quote"
              className="inline-flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-white px-6 py-3 rounded font-bold transition shadow-sm">
              Request a quote <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>
    </LitLabLayout>
  );
}

function Block({ label, body, tone, checkmark }) {
  const bg = {
    rose: "border-rose-200 bg-rose-50", sky: "border-sky-200 bg-sky-50",
    amber: "border-amber-200 bg-amber-50", emerald: "border-emerald-200 bg-emerald-50",
  }[tone];
  const kicker = {
    rose: "text-rose-700", sky: "text-sky-700",
    amber: "text-amber-700", emerald: "text-emerald-700",
  }[tone];
  return (
    <div className={`rounded-2xl border p-6 ${bg}`}>
      <div className={`text-xs uppercase tracking-[0.3em] font-bold ${kicker} flex items-center gap-2`}>
        {checkmark && <CheckCircle2 className="w-4 h-4" />}
        {label}
      </div>
      <div className="mt-3 text-slate-800 leading-relaxed">{body}</div>
    </div>
  );
}
